import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getSocket } from '../services/socket';
import ChallengeCreate from '../components/ChallengeCreate';
import ChallengeFriendlyCreate from '../components/ChallengeFriendlyCreate';
import ChallengeAccept from '../components/ChallengeAccept';
import '../styles/Challenges.css';

function Challenges() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [challenges, setChallenges] = useState([]);
  const [pendingDirectChallenges, setPendingDirectChallenges] = useState([]);
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFriendlyModal, setShowFriendlyModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [creatorSearch, setCreatorSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalizedSearch = creatorSearch.trim().toLowerCase();
  const renderAiBadge = (isAi) => (isAi ? <span className="challenge-ai-badge">AI Player</span> : null);
  const challengeMatchesSearch = (challenge) => {
    if (!normalizedSearch) return true;
    const name = String(challenge.creator_username || '').toLowerCase();
    return name.includes(normalizedSearch);
  };
  const getChallengeMode = (challenge) => (Number(challenge?.wager || 0) > 0 ? 'betting' : 'friendly');
  const getDisplayGameCount = (challenge) => {
    const count = Number(challenge?.num_games || 1);
    if (!Number.isFinite(count) || count <= 1) return null;
    return Math.floor(count);
  };

  // Fetch open and direct challenges + setup polling
  useEffect(() => {
    fetchChallenges();
    fetchActiveChallenge();

    // Listen for new challenges
    const socket = getSocket();
    if (socket) {
      const refreshChallenges = () => {
        fetchChallenges();
        fetchActiveChallenge();
      };

      socket.on('challenge:new', refreshChallenges);
      socket.on('challenge:direct:new', refreshChallenges);
      socket.on('challenge:accepted', refreshChallenges);
      socket.on('challenge:started', refreshChallenges);

      return () => {
        socket.off('challenge:new', refreshChallenges);
        socket.off('challenge:direct:new', refreshChallenges);
        socket.off('challenge:accepted', refreshChallenges);
        socket.off('challenge:started', refreshChallenges);
      };
    }
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      fetchChallenges();
      fetchActiveChallenge();
    }, 15000);
    return () => clearInterval(id);
  }, []);

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      // Fetch both open challenges and pending direct challenges
      const [openRes, directRes] = await Promise.all([
        api.get('/challenges/open'),
        api.get('/challenges/pending/direct')
      ]);
      setChallenges(openRes.data);
      setPendingDirectChallenges(directRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch challenges');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveChallenge = async () => {
    try {
      const response = await api.get('/challenges/active');
      setActiveChallenge(response?.data || null);
    } catch (err) {
      setActiveChallenge(null);
    }
  };

  const handleCreateChallenge = async (wager, options = {}) => {
    try {
      const response = await api.post('/challenges', { wager, ...options });
      setShowCreateModal(false);
      setShowFriendlyModal(false);
      
      // Emit socket event to broadcast new challenge
      const socket = getSocket();
      if (socket) socket.emit('challenge:created', { challengeId: response.data.id });
      
      await fetchChallenges();
      await fetchActiveChallenge();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create challenge');
    }
  };

  const handleAcceptChallenge = async (challenge) => {
    setSelectedChallenge(challenge);
    setShowAcceptModal(true);
  };

  const confirmAccept = async () => {
    try {
      const response = await api.post(`/challenges/${selectedChallenge.id}/accept`);
      const totalRisk = Number(selectedChallenge?.wager || 0) * Number(selectedChallenge?.num_games || 1);
      
      // Deduct wager from user balance
      updateUser({
        ...user,
        balance: Number(user.balance || 0) - totalRisk
      });

      // Emit socket event
      const socket = getSocket();
      if (socket) socket.emit('challenge:accepted', { challengeId: response.data.id });
      
      setShowAcceptModal(false);
      setSelectedChallenge(null);
      
      // Navigate to the appropriate game UI (Whot uses the Whot room)
      if ((selectedChallenge.game || '').toLowerCase() === 'whot') {
        navigate(`/whot-online/room/${selectedChallenge.id}`);
      } else {
        navigate(`/challenges/${selectedChallenge.id}`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to accept challenge';
      setError(errorMsg);

      // If user tried to accept their own challenge (or the challenge is no longer valid),
      // close the modal since there’s nothing to confirm.
      if (
        errorMsg.toLowerCase().includes('cannot accept your own challenge') ||
        errorMsg.toLowerCase().includes('cannot accept your own') ||
        errorMsg.toLowerCase().includes('no longer available') ||
        errorMsg.toLowerCase().includes('not found')
      ) {
        setShowAcceptModal(false);
        setSelectedChallenge(null);
      }
    }
  };

  const handleRejectDirectChallenge = async (challengeId) => {
    try {
      await api.delete(`/challenges/${challengeId}`);
      await fetchChallenges();
      await fetchActiveChallenge();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject challenge');
    }
  };

  const handleCancelChallenge = async (challengeId) => {
    try {
      await api.delete(`/challenges/${challengeId}`);
      await fetchChallenges();
      await fetchActiveChallenge();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel challenge');
    }
  };

  const challengeSorter = (left, right) => {
    const leftCreated = new Date(left?.created_at || 0).getTime();
    const rightCreated = new Date(right?.created_at || 0).getTime();
    return rightCreated - leftCreated;
  };

  const myPendingOpenChallenges = (challenges || []).filter(
    (challenge) => Number(challenge.creator_id) === Number(user?.id)
  );

  const activeChallengeCandidates = [
    ...(activeChallenge ? [activeChallenge] : []),
    ...myPendingOpenChallenges,
  ];

  const myActiveById = new Map();
  activeChallengeCandidates.forEach((challenge) => {
    if (challenge?.id) myActiveById.set(challenge.id, challenge);
  });

  const myActiveChallenges = Array.from(myActiveById.values())
    .filter(challengeMatchesSearch)
    .sort(challengeSorter);

  const availableOpenChallenges = (challenges || [])
    .filter((challenge) => Number(challenge.creator_id) !== Number(user?.id))
    .filter(challengeMatchesSearch)
    .map((challenge) => ({ ...challenge, __kind: 'open' }));

  const availableDirectChallenges = (pendingDirectChallenges || [])
    .filter(challengeMatchesSearch)
    .map((challenge) => ({ ...challenge, __kind: 'direct' }));

  const availableChallenges = [...availableDirectChallenges, ...availableOpenChallenges].sort(challengeSorter);

  if (loading) {
    return <div className="challenges-container"><p>Loading challenges...</p></div>;
  }

  return (
    <div className="challenges-container">
      <div className="challenges-header">
        <h1>Dice</h1>
        <div className="challenges-actions">
          <button 
            className="btn-create-challenge"
            onClick={() => setShowCreateModal(true)}
          >
            + Real Challenge
          </button>
          <button 
            className="btn-create-challenge btn-create-friendly"
            onClick={() => setShowFriendlyModal(true)}
          >
            + Friendly Challenge
          </button>
        </div>
      </div>

      <div className="challenges-search">
        <input
          className="challenges-search-input"
          type="text"
          value={creatorSearch}
          onChange={(e) => setCreatorSearch(e.target.value)}
          placeholder="Search by creator name"
          aria-label="Search challenges by creator name"
        />
      </div>

      {error && <div className="error-message">{error}</div>}

      {activeChallenge && (
        <div className="open-challenges">
          <h2>Current Dice Match</h2>
          <div className="challenge-row">
            <div className="challenge-row-main">
              <div className="challenge-row-title">
                {activeChallenge.creator_username || user?.username}
                <span className="challenge-id">#{activeChallenge.id}</span>
                <span className="challenge-status-inline">Status: {activeChallenge.status}</span>
              </div>
              <div className="challenge-row-meta">
                <span className={`challenge-wager ${Number(activeChallenge?.wager || 0) > 0 ? 'betting' : 'friendly'}`}>
                  Wager: {activeChallenge.wager} wikipenny
                </span>
                <span>
                  {String(activeChallenge.status || '').toLowerCase() === 'in_progress'
                    ? 'Your dice match is live now'
                    : 'Your dice challenge is waiting'}
                </span>
              </div>
            </div>
            <div className="challenge-row-actions">
              <button
                className="btn-accept"
                onClick={() => navigate(`/challenges/${activeChallenge.id}`)}
              >
                Resume
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="open-challenges">
        <h2>🎲 Your Active Challenge</h2>
        {myActiveChallenges.length === 0 ? (
          <div className="no-challenges">
            <p>No active challenges yet</p>
            <p>Create or join a challenge to begin.</p>
          </div>
        ) : (
          <div className="challenges-list">
            {myActiveChallenges.map((challenge) => {
              const mode = getChallengeMode(challenge);
              const isBetting = mode === 'betting';
              const canCancel = challenge.status === 'pending' && Number(challenge.creator_id) === Number(user?.id);
              const displayGames = getDisplayGameCount(challenge);

              return (
                <div key={challenge.id} className="challenge-row">
                  <div className="challenge-row-main">
                    <div className="challenge-row-title">
                      {displayGames && <span className="challenge-game-prefix">G{displayGames}</span>}
                      {challenge.creator_username || user?.username}
                      {renderAiBadge(challenge.creator_is_ai || challenge.opponent_is_ai)}
                      <span className="challenge-id">#{challenge.id}</span>
                      <span className="challenge-status-inline">Status: {challenge.status}</span>
                      <span className={`challenge-mode ${isBetting ? 'betting' : 'friendly'}`}>
                        {isBetting ? '⚠️ Betting' : '✅ Friendly'}
                      </span>
                    </div>
                    <div className="challenge-row-meta">
                      <span className={`challenge-wager ${isBetting ? 'betting' : 'friendly'}`}>
                        Wager: {challenge.wager} wikipenny
                      </span>
                    </div>
                  </div>
                  <div className="challenge-row-actions">
                    <button
                      className="btn-accept"
                      onClick={() => navigate(`/challenges/${challenge.id}`)}
                    >
                      Play
                    </button>
                    {canCancel && (
                      <button
                        className="btn-reject"
                        onClick={() => handleCancelChallenge(challenge.id)}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="open-challenges">
        <h2>Available Challenges</h2>
        {availableChallenges.length === 0 ? (
          <div className="no-challenges">
            <p>{normalizedSearch ? 'No matching challenges' : 'No available challenges yet'}</p>
            {!normalizedSearch && <p>Create one to get started.</p>}
          </div>
        ) : (
          <div className="challenges-list">
            {availableChallenges.map((challenge) => {
              const mode = getChallengeMode(challenge);
              const isBetting = mode === 'betting';
              const isDirect = challenge.__kind === 'direct';
              const displayGames = getDisplayGameCount(challenge);

              return (
                <div key={challenge.id} className="challenge-row">
                  <div className="challenge-row-main">
                    <div className="challenge-row-title">
                      {displayGames && <span className="challenge-game-prefix">G{displayGames}</span>}
                      {challenge.creator_username}
                      {renderAiBadge(challenge.creator_is_ai)}
                      <span className="challenge-id">#{challenge.id}</span>
                      <span className="challenge-status-inline">Status: {challenge.status}</span>
                      <span className={`challenge-mode ${isBetting ? 'betting' : 'friendly'}`}>
                        {isBetting ? '⚠️ Betting' : '✅ Friendly'}
                      </span>
                    </div>
                    <div className="challenge-row-meta">
                      <span className={`challenge-wager ${isBetting ? 'betting' : 'friendly'}`}>
                        Wager: {challenge.wager} wikipenny
                      </span>
                      <span>{isDirect ? 'Direct Challenge' : 'Open Challenge'}</span>
                    </div>
                  </div>
                  <div className="challenge-row-actions">
                    <button
                      className="btn-accept"
                      onClick={() => handleAcceptChallenge(challenge)}
                    >
                      Join
                    </button>
                    {isDirect && (
                      <button
                        className="btn-reject"
                        onClick={() => handleRejectDirectChallenge(challenge.id)}
                      >
                        Reject
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreateModal && (
        <ChallengeCreate
          userBalance={user.balance}
          onCreate={handleCreateChallenge}
          onCancel={() => setShowCreateModal(false)}
        />
      )}

      {showFriendlyModal && (
        <ChallengeFriendlyCreate
          onCreate={handleCreateChallenge}
          onCancel={() => setShowFriendlyModal(false)}
        />
      )}

      {showAcceptModal && selectedChallenge && (
        <ChallengeAccept
          challenge={selectedChallenge}
          userBalance={user.balance}
          onAccept={confirmAccept}
          onCancel={() => {
            setShowAcceptModal(false);
            setSelectedChallenge(null);
          }}
        />
      )}
    </div>
  );
}

export default Challenges;

